import { IDxfBounds } from './dxf-entities';
import { DXF_NO_OWNER, IDxfOut, beginSection, coord, endSection, int, pair, point, real } from './dxf-out';
import { IDxfBlockOwners, IDxfLayoutHandles } from './dxf-tables';

/** The handles reserved for the OBJECTS section before the tables point at them. */
export interface IDxfObjectHandles extends IDxfLayoutHandles {
    root: string;
    groups: string;
    layouts: string;
}

/** One LAYOUT object: its handle, name, tab order, the paper it plots on and its block record. */
interface ILayout {
    handle: string;
    name: string;
    tabOrder: number;
    owner: string;
    paper: { width: number; height: number };
    /** Plot type 74: 0 the display, 5 the layout. */
    plotType: number;
    /** The layout's paper viewport, named as its last active one; none for model space. */
    lastViewport: string | null;
    extents: IDxfBounds;
}

/** Plot settings flags 70 of a layout: print lineweights (128), plot paper space last (512)… as AutoCAD writes them. */
const MODEL_PLOT_FLAGS: number = 1024;
const PAPER_PLOT_FLAGS: number = 672;

function dictionary(out: IDxfOut, handle: string, owner: string, entries: readonly [string, string][]): void {
    pair(out, 0, 'DICTIONARY');
    pair(out, 5, handle);
    pair(out, 330, owner);
    pair(out, 100, 'AcDbDictionary');
    int(out, 281, 1);
    for (const [name, value] of entries) {
        pair(out, 3, name);
        pair(out, 350, value);
    }
}

function plotSettings(out: IDxfOut, layout: ILayout): void {
    pair(out, 100, 'AcDbPlotSettings');
    pair(out, 1, '');
    pair(out, 2, 'none_device');
    pair(out, 4, `ISO_A2_(${String(layout.paper.width)}.00_x_${String(layout.paper.height)}.00_MM)`);
    pair(out, 6, '');
    for (const code of [40, 41, 42, 43]) {
        coord(out, code, 0);
    }
    coord(out, 44, layout.paper.width);
    coord(out, 45, layout.paper.height);
    for (const code of [46, 47, 48, 49, 140, 141]) {
        coord(out, code, 0);
    }
    real(out, 142, 1);
    real(out, 143, 1);
    int(out, 70, layout.plotType === 5 ? PAPER_PLOT_FLAGS : MODEL_PLOT_FLAGS);
    // Paper units: millimetres.
    int(out, 72, 1);
    int(out, 73, 0);
    int(out, 74, layout.plotType);
    pair(out, 7, '');
    // Standard scale 16: 1 : 1.
    int(out, 75, 16);
    int(out, 76, 0);
    int(out, 77, 2);
    int(out, 78, 300);
    real(out, 147, 1);
    real(out, 148, 0);
    real(out, 149, 0);
}

function writeLayout(out: IDxfOut, layout: ILayout, dictionaryHandle: string): void {
    pair(out, 0, 'LAYOUT');
    pair(out, 5, layout.handle);
    pair(out, 330, dictionaryHandle);
    plotSettings(out, layout);
    pair(out, 100, 'AcDbLayout');
    pair(out, 1, layout.name);
    int(out, 70, 1);
    int(out, 71, layout.tabOrder);
    coord(out, 10, 0);
    coord(out, 20, 0);
    coord(out, 11, layout.paper.width);
    coord(out, 21, layout.paper.height);
    point(out, 12, [0, 0]);
    point(out, 14, [layout.extents.minX, layout.extents.minY]);
    point(out, 15, [layout.extents.maxX, layout.extents.maxY]);
    real(out, 146, 0);
    point(out, 13, [0, 0]);
    point(out, 16, [1, 0]);
    point(out, 17, [0, 1]);
    int(out, 76, 1);
    pair(out, 330, layout.owner);
    if (layout.lastViewport !== null) {
        pair(out, 331, layout.lastViewport);
    }
}

/** What the OBJECTS section names: the layouts' owners, sheets and extents. */
export interface IDxfLayoutsInput {
    owners: IDxfBlockOwners;
    paper: { width: number; height: number };
    sheetName: string;
    paperViewport: string;
    modelExtents: IDxfBounds;
}

/**
 * The OBJECTS section: the root dictionary with the group and layout dictionaries, and the two
 * layouts — «Model» and the sheet in paper space.
 */
export function writeObjects(out: IDxfOut, handles: IDxfObjectHandles, input: IDxfLayoutsInput): void {
    beginSection(out, 'OBJECTS');
    dictionary(out, handles.root, DXF_NO_OWNER, [
        ['ACAD_GROUP', handles.groups],
        ['ACAD_LAYOUT', handles.layouts],
    ]);
    dictionary(out, handles.groups, handles.root, []);
    dictionary(out, handles.layouts, handles.root, [
        ['Model', handles.modelLayout],
        [input.sheetName, handles.paperLayout],
    ]);
    writeLayout(
        out,
        {
            handle: handles.modelLayout,
            name: 'Model',
            tabOrder: 0,
            owner: input.owners.modelSpace,
            paper: input.paper,
            plotType: 0,
            lastViewport: null,
            extents: input.modelExtents,
        },
        handles.layouts
    );
    writeLayout(
        out,
        {
            handle: handles.paperLayout,
            name: input.sheetName,
            tabOrder: 1,
            owner: input.owners.paperSpace,
            paper: input.paper,
            plotType: 5,
            lastViewport: input.paperViewport,
            extents: { minX: 0, minY: 0, maxX: input.paper.width, maxY: input.paper.height },
        },
        handles.layouts
    );
    endSection(out);
}
